import type {
  RankedSearchResult,
  SearchContentType,
  SearchDocument,
  SearchIndex,
} from './searchTypes';

const TOKEN_PATTERN = /[a-z0-9]+(?:'[a-z0-9]+)?/g;
const STOP_WORDS = new Set([
  'a', 'an', 'and', 'are', 'as', 'at', 'be', 'been', 'but', 'by', 'for',
  'from', 'had', 'has', 'have', 'he', 'her', 'his', 'i', 'in', 'is', 'it',
  'its', 'me', 'my', 'not', 'of', 'on', 'or', 'our', 'she', 'so', 'that',
  'the', 'their', 'them', 'they', 'this', 'to', 'us', 'was', 'we', 'were',
  'what', 'when', 'where', 'which', 'who', 'will', 'with', 'you', 'your',
]);

function tokens(value: string): string[] {
  return (value.toLocaleLowerCase().match(TOKEN_PATTERN) ?? []).filter(
    (token) => token.length > 1 && !STOP_WORDS.has(token),
  );
}

function normalizePhrase(value: string): string {
  return value
    .toLocaleLowerCase()
    .replace(/[“”"]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function searchableFields(
  document: SearchDocument,
): Record<Exclude<SearchContentType, 'all'>, string> {
  return {
    talk: [document.title, document.text, ...document.callsToAction].join(' '),
    speaker: document.speaker,
    theme: document.themes.join(' '),
    scripture: document.scriptures.join(' '),
    quote: document.quotes.join(' '),
  };
}

function matchingTypes(
  document: SearchDocument,
  queryTokens: readonly string[],
  phrase: string,
): Exclude<SearchContentType, 'all'>[] {
  const matches: Exclude<SearchContentType, 'all'>[] = [];
  const fields = searchableFields(document);
  for (const [type, value] of Object.entries(fields) as [
    Exclude<SearchContentType, 'all'>,
    string,
  ][]) {
    const normalized = value.toLocaleLowerCase();
    if (
      (phrase.length > 2 && normalized.includes(phrase)) ||
      queryTokens.some((token) => normalized.includes(token))
    ) {
      matches.push(type);
    }
  }
  return matches;
}

export async function loadSearchIndex(conferenceId: string): Promise<SearchIndex> {
  const response = await fetch(
    `/data/conference/${encodeURIComponent(conferenceId)}/search-index.json`,
  );
  if (!response.ok) {
    throw new Error(`Search index request failed with ${String(response.status)}.`);
  }
  const value = (await response.json()) as SearchIndex;
  if (
    value.schemaVersion !== '2.0.0' ||
    value.documentCount !== value.documents.length
  ) {
    throw new Error('Search index has an unsupported schema.');
  }
  return value;
}

export function lexicalSearch(
  index: SearchIndex,
  query: string,
  type: SearchContentType = 'all',
  limit = 50,
): RankedSearchResult[] {
  const queryTokens = [...new Set(tokens(query))];
  const phrase = normalizePhrase(query);
  if (queryTokens.length === 0 && phrase.length < 2) return [];

  const scores = new Map<number, number>();
  const documentCount = index.documents.length;
  for (const term of queryTokens) {
    const postings = index.postings[term] ?? [];
    const inverseDocumentFrequency = Math.log(
      1 + (documentCount - postings.length + 0.5) / (postings.length + 0.5),
    );
    for (const [documentIndex, frequency] of postings) {
      const documentLength = Math.max(
        1,
        tokens(
          [
            index.documents[documentIndex].text,
            index.documents[documentIndex].title,
            index.documents[documentIndex].speaker,
          ].join(' '),
        ).length,
      );
      const normalization =
        frequency +
        1.2 *
          (0.25 + 0.75 * (documentLength / index.averageDocumentLength));
      const bm25 = inverseDocumentFrequency * ((frequency * 2.2) / normalization);
      scores.set(documentIndex, (scores.get(documentIndex) ?? 0) + bm25);
    }
  }

  const results = [...scores.entries()]
    .map(([documentIndex, baseScore]) => {
      const document = index.documents[documentIndex];
      const matchedTypes = matchingTypes(document, queryTokens, phrase);
      let score = baseScore;
      const text = document.text.toLocaleLowerCase();
      const title = document.title.toLocaleLowerCase();
      const speaker = document.speaker.toLocaleLowerCase();
      if (phrase.length > 4 && text.includes(phrase)) score += 100;
      if (phrase.length > 4 && title.includes(phrase)) score += 30;
      if (phrase.length > 4 && speaker.includes(phrase)) score += 24;
      if (queryTokens.every((token) => text.includes(token))) score += 8;
      score += queryTokens.filter((token) => title.includes(token)).length * 3;
      score += queryTokens.filter((token) => speaker.includes(token)).length * 2;
      return { document, score, matchedTypes };
    })
    .filter(
      (result) =>
        type === 'all' ||
        result.matchedTypes.includes(type),
    )
    .sort(
      (first, second) =>
        second.score - first.score ||
        first.document.id.localeCompare(second.document.id),
    )
    .slice(0, limit);

  return results.map((result, rank) => ({
    ...result,
    rank: rank + 1,
    sources: ['lexical'],
  }));
}

export function queryTerms(query: string): string[] {
  return [...new Set(tokens(query))].sort(
    (first, second) => second.length - first.length,
  );
}
